import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileText, Bot, X, ChevronRight } from "lucide-react";

interface Props {
  projectName: string;
  onDismiss: () => void;
}

export default function BAIntroPanel({ projectName, onDismiss }: Props) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: <ShieldCheck className="h-8 w-8 text-accent" />,
      title: "Welcome to the BA Healthcare Intelligence Layer",
      body: `You're now inside the compliance-aware analysis module for "${projectName}". This is where raw ideas become structured, auditable, regulation-ready requirements — purpose-built for healthcare environments where getting it wrong has real consequences.`,
    },
    {
      icon: <FileText className="h-8 w-8 text-accent" />,
      title: "How requirements work here",
      body: "Every requirement you create is structured: it captures the feature, the channel it touches (email, SMS, portal), whether PHI is involved, and the market it applies to. This structure is what allows the rules engine to evaluate it — not guesswork, deterministic logic.",
    },
    {
      icon: <Bot className="h-8 w-8 text-accent" />,
      title: "The system works even when AI doesn't",
      body: "Compliance checks run against a deterministic rules engine first — HIPAA, state law, internal policy. AI sits on top to explain and accelerate, never to decide. Every agent action is logged. Every rule that fires is traceable to a reference. This is what production healthcare systems actually need.",
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="max-w-lg w-full border border-border rounded-lg bg-card shadow-sm animate-fade-in">
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === step ? "w-6 bg-accent" : i < step ? "w-6 bg-accent/30" : "w-6 bg-muted"
                }`}
              />
            ))}
          </div>
          <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-8 space-y-4">
          <div className="flex items-center gap-3">
            {current.icon}
            <h2
              className="text-lg font-bold text-foreground leading-snug"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {current.title}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-between items-center">
          <button
            onClick={() => step > 0 && setStep(step - 1)}
            className={`text-xs text-muted-foreground hover:text-foreground transition-colors ${step === 0 ? "invisible" : ""}`}
          >
            ← Back
          </button>
          {isLast ? (
            <Button size="sm" onClick={onDismiss} className="gap-1.5">
              Start working <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setStep(step + 1)} className="gap-1.5">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
